import { useHandler } from "@redotech/react-util/hook";
import { RedoShopifyDeliveryMethodType } from "@redotech/redo-fulfillment-common/fulfillment-delivery-method-type";
import { FulfillmentOrderAddress } from "@redotech/redo-fulfillment-customs-common/fulfillment-order-address";
import { FulfillmentGroupData } from "@redotech/redo-fulfillment-group-common/fulfillment-group";
import { SelectedShippingRateSetBy } from "@redotech/redo-fulfillment-group-common/selected-shipping-rate-set-by";
import { Preset } from "@redotech/redo-fulfillment-parcel-common/presets";
import { FulfillmentGroupUpdate } from "@redotech/redo-merchant-fulfillment-rpc/schema/update-fulfillment-groups";
import { OutboundRateOption } from "../common/util";

function getRateAsNumber(rate: string | number | undefined): number {
  if (typeof rate === "number") {
    return rate;
  }

  if (typeof rate === "string") {
    const parsedRate = parseFloat(rate);
    if (!isNaN(parsedRate)) {
      return parsedRate;
    }
    console.error("Parsed rate is NaN", { rate });
    return 0;
  }

  // rate is undefined or null-like
  return 0;
}

export const useRateActions = ({
  fulfillmentGroups,
  updateRows,
  setSelectedPreset,
  openEditAddressModal,
  onEditAddress,
  onUseAutoCleanedAddress,
}: {
  fulfillmentGroups: readonly FulfillmentGroupData[];
  updateRows: (updates: FulfillmentGroupUpdate[]) => void;
  setSelectedPreset: (preset: Preset | undefined) => void;
  openEditAddressModal: (
    address: FulfillmentOrderAddress | null,
    onAddressUpdate: (address: FulfillmentOrderAddress) => Promise<void>,
    onUseAutoCleanedAddress: (useAutoCleanedAddress: boolean) => void,
  ) => void;
  onEditAddress: (address: FulfillmentOrderAddress) => Promise<void>;
  onUseAutoCleanedAddress?: (useAutoCleanedAddress: boolean) => Promise<void>;
}) => {
  const onSelectedRateChange = useHandler(
    (option: OutboundRateOption | RedoShopifyDeliveryMethodType) => {
      const updates: FulfillmentGroupUpdate[] = [];

      for (const group of fulfillmentGroups) {
        if (typeof option === "string") {
          const doPushUpdate = () => {
            updates.push({
              fulfillmentGroupId: group._id,
              selectedShippingRate: null,
              overrideDeliveryMethod: option,
            });
          };

          doPushUpdate();

          // Check if we should prompt the user to enter an address
          if (
            option === RedoShopifyDeliveryMethodType.Shipping &&
            fulfillmentGroups.length === 1 &&
            !group.destinationAddress?.address1
          ) {
            openEditAddressModal(
              group.destinationAddress,
              async (address) => {
                await onEditAddress(address);
              },
              async (useAutoCleanedAddress) => {
                if (onUseAutoCleanedAddress) {
                  await onUseAutoCleanedAddress(useAutoCleanedAddress);
                }
              },
            );
          }
        } else {
          // Find the matching rate in this group's available rates
          // based on the carrier, service, and whether it's a redo rate
          const matchingRates =
            group.availableShipmentRates?.rates.filter(
              (rate) =>
                rate.carrier === option.carrier &&
                rate.service === option.service &&
                rate.carrier_account_id === option.carrier_account_id,
            ) || [];

          // Choose the cheapest matching rate for this group
          const cheapestRate =
            matchingRates.length > 0
              ? matchingRates.reduce(
                  (cheapest, rate) =>
                    // @ts-expect-error TODO: noUncheckedIndexedAccess FIXME
                    Number(cheapest.rate) <= Number(rate.rate)
                      ? cheapest
                      : rate,
                  matchingRates[0],
                )
              : null;

          updates.push({
            selectedShippingRateSetBy: SelectedShippingRateSetBy.USER,
            fulfillmentGroupId: group._id,
            fulfillmentInfo: cheapestRate
              ? {
                  carrier: cheapestRate.carrier,
                  service: cheapestRate.service,
                  numericRate: parseFloat(cheapestRate.rate),
                  estimatedDeliveryDays: cheapestRate.delivery_days,
                }
              : { carrier: option.carrier, service: option.service },
            selectedShippingRate: cheapestRate
              ? {
                  carrier: cheapestRate.carrier,
                  service: cheapestRate.service,
                  carrier_account_id: cheapestRate.carrier_account_id,
                  rate: getRateAsNumber(cheapestRate.rate),
                }
              : {
                  carrier: option.carrier,
                  service: option.service,
                  carrier_account_id: option.carrier_account_id,
                  rate: getRateAsNumber(option.rate),
                },
            overrideDeliveryMethod: RedoShopifyDeliveryMethodType.Shipping,
          });
        }
      }
      setSelectedPreset(undefined);
      updateRows(updates);
    },
  );

  return { onSelectedRateChange };
};
